<script setup lang="ts">
import { Button as SonikButton } from '@loopreturns/sonik/button';
import { Menubar } from '@loopreturns/sonik/menubar';
import type { MenuItem } from 'primevue/menuitem';
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import MxToolLogo from '@/components/layout/MxToolLogo.vue';
import { ROUTE_NAMES } from '@/router';
import { useAuthStore } from '@/stores/auth';
import { useUiStore } from '@/stores/ui';

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();
const uiStore = useUiStore();

function navigateTo (name: (typeof ROUTE_NAMES)[keyof typeof ROUTE_NAMES]) {
  setTimeout(() => {
    router.push({ name }).catch(() => {});
  }, 0);
}

function isActive (prefix: string): boolean {
  return route.path.startsWith(prefix);
}

function isActionsActive (): boolean {
  return ['/workflow-logs', '/gdpr-data', '/demo-shops', '/loop-anywhere-setup', '/sendcloud', '/early-access', '/organization', '/merchant-notification', '/loop-assistant', '/background-agents', '/experiments', '/checkout-plus-billing', '/bulk-destination-address-update']
    .some((path) => route.path.startsWith(path));
}

const menuItems = computed<MenuItem[]>(() => [
  {
    label: 'Shops',
    icon: 'pi pi-shop',
    command: () => navigateTo(ROUTE_NAMES.HOME),
    active: isActive('/shops'),
  },
  {
    label: 'Returns',
    icon: 'pi pi-replay',
    command: () => navigateTo(ROUTE_NAMES.RETURNS),
    active: isActive('/returns') || isActive('/orders'),
  },
  {
    label: 'Shipping',
    icon: 'pi pi-truck',
    command: () => navigateTo(ROUTE_NAMES.SHIPPING),
    active: isActive('/shipping'),
  },
  {
    label: 'Actions',
    icon: 'pi pi-ellipsis-h',
    active: isActionsActive(),
    items: [
      { label: 'Workflow Logs', icon: 'pi pi-list', command: () => navigateTo(ROUTE_NAMES.WORKFLOW_LOGS) },
      { label: 'Loop Assistant', icon: 'pi pi-sparkles', command: () => navigateTo(ROUTE_NAMES.LOOP_ASSISTANT) },
      { label: 'Knowledge Snippets', icon: 'pi pi-book', command: () => navigateTo(ROUTE_NAMES.KNOWLEDGE_SNIPPETS) },
      { label: 'Background Agents', icon: 'pi pi-bolt', command: () => navigateTo(ROUTE_NAMES.BACKGROUND_AGENTS_FLEET) },
      { label: 'Experiments', icon: 'pi pi-chart-bar', command: () => navigateTo(ROUTE_NAMES.EXPERIMENTS) },
      { label: 'GDPR Data', icon: 'pi pi-shield', command: () => navigateTo(ROUTE_NAMES.GDPR) },
      { label: 'Demo Mode Shops', icon: 'pi pi-desktop', command: () => navigateTo(ROUTE_NAMES.DEMO_SHOPS) },
      { label: 'Loop Anywhere Setup', icon: 'pi pi-database', command: () => navigateTo(ROUTE_NAMES.LOOP_ANYWHERE_SETUP) },
      { label: 'Sendcloud', icon: 'pi pi-cloud', command: () => navigateTo(ROUTE_NAMES.SENDCLOUD) },
      { label: 'Early Access', icon: 'pi pi-star', command: () => navigateTo(ROUTE_NAMES.EARLY_ACCESS) },
      { label: 'Organizations', icon: 'pi pi-building', command: () => navigateTo(ROUTE_NAMES.ORGANIZATIONS) },
      { label: 'Merchant Notifications', icon: 'pi pi-bell', command: () => navigateTo(ROUTE_NAMES.MERCHANT_NOTIFICATIONS) },
      { label: 'Checkout+ Billing', icon: 'pi pi-credit-card', command: () => navigateTo(ROUTE_NAMES.CHECKOUT_PLUS_BILLING) },
      { label: 'Bulk Destination Address', icon: 'pi pi-map-marker', command: () => navigateTo(ROUTE_NAMES.BULK_DESTINATION_ADDRESS_UPDATE) },
    ],
  },
]);

const menubarPt = {
  root: {
    style: {
      borderRadius: '0',
      borderLeft: 'none',
      borderRight: 'none',
      borderTop: 'none',
    },
  },
};

const userName = computed(() => authStore.userData?.name ?? 'User');
</script>

<template>
  <div class="sticky top-0 z-100 shrink-0">
    <Menubar
      :model="menuItems"
      :pt="menubarPt"
    >
      <template #start>
        <router-link
          to="/shops"
          class="mr-6 flex h-8 shrink-0 items-center px-2 sm:h-9"
        >
          <MxToolLogo />
        </router-link>
      </template>

      <template #item="{ item, props, hasSubmenu, root }">
        <a
          v-ripple
          v-bind="props.action"
          class="flex items-center gap-3 rounded-md px-3 py-2 font-medium transition-colors"
          :class="[
            item.active
              ? 'bg-primary-50 text-primary-700 dark:bg-surface-800 dark:text-surface-0 dark:[&_span.pi]:text-primary-300'
              : 'text-surface-700 hover:bg-surface-100 dark:text-surface-200 dark:hover:bg-surface-800 dark:hover:text-surface-0 dark:[&_span.pi]:text-surface-400',
          ]"
        >
          <span
            v-if="item.icon"
            :class="item.icon"
          />
          <span>{{ item.label }}</span>
          <i
            v-if="hasSubmenu"
            :class="['pi ml-auto text-xs', root ? 'pi-angle-down' : 'pi-angle-right']"
          />
        </a>
      </template>

      <template #end>
        <div class="flex items-center gap-3">
          <SonikButton
            :icon="uiStore.isDark ? 'pi pi-sun' : 'pi pi-moon'"
            :aria-label="uiStore.isDark ? 'Switch to light mode' : 'Switch to dark mode'"
            text
            size="small"
            severity="secondary"
            @click="uiStore.toggleColorScheme()"
          />
          <span class="hidden text-sm text-surface-500 sm:inline">
            {{ userName }}
          </span>
          <SonikButton
            icon="pi pi-sign-out"
            label="Logout"
            text
            size="small"
            severity="secondary"
            @click="authStore.logout()"
          />
        </div>
      </template>
    </Menubar>
  </div>
</template>
